/**
 * Form Templates Zustand Store
 *
 * Manages state for Global Form Templates including:
 * - Template list with filters and pagination
 * - Active template details
 * - Template CRUD operations
 * - Visibility management
 */
import { create } from 'zustand';
import { getApiClient } from '@respectly/api-client';
import type {
  GlobalTemplate,
  PaginationMeta,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface FormTemplateFilters {
  search?: string;
  category?: string | null; // popular, healthcare, business, events, feedback
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  isActive?: boolean;
}

interface CreateFormTemplateDto {
  name: string;
  description?: string;
  content: string; // JSON string
  category?: string;
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  visibleToPlans?: string[];
  visibleToTenants?: string[];
  isActive?: boolean;
}

interface UpdateFormTemplateDto {
  name?: string;
  description?: string;
  content?: string; // JSON string
  category?: string;
  visibility?: 'all' | 'plans' | 'tenants' | 'none';
  visibleToPlans?: string[];
  visibleToTenants?: string[];
  isActive?: boolean;
}

interface FormTemplatesError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface FormTemplatesState {
  // Templates List
  templates: GlobalTemplate[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: FormTemplatesError | null;

  // Filters
  filters: FormTemplateFilters;

  // Active Template
  activeTemplateId: string | null;
  activeTemplate: GlobalTemplate | null;
  isLoadingTemplate: boolean;
  templateError: FormTemplatesError | null;

  // Action States
  isPerformingAction: boolean;
  actionError: FormTemplatesError | null;

  // Actions - List
  loadTemplates: (page?: number) => Promise<void>;
  refreshTemplates: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof FormTemplateFilters>(key: K, value: FormTemplateFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active Template
  loadTemplate: (id: string) => Promise<void>;
  selectTemplate: (id: string) => void;
  clearActiveTemplate: () => void;

  // Actions - CRUD
  createTemplate: (dto: CreateFormTemplateDto) => Promise<string | null>;
  updateTemplate: (id: string, dto: UpdateFormTemplateDto) => Promise<boolean>;
  duplicateTemplate: (id: string, newName?: string) => Promise<string | null>;
  deleteTemplate: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Templates List
  templates: [] as GlobalTemplate[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as FormTemplatesError | null,

  // Filters
  filters: {} as FormTemplateFilters,

  // Active Template
  activeTemplateId: null as string | null,
  activeTemplate: null as GlobalTemplate | null,
  isLoadingTemplate: false,
  templateError: null as FormTemplatesError | null,

  // Action States
  isPerformingAction: false,
  actionError: null as FormTemplatesError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useFormTemplatesStore = create<FormTemplatesState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadTemplates: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();

      // Always filter by type=form
      params.set('type', 'form');
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.visibility) params.set('visibility', filters.visibility);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));

      const response = await api.get<{
        success: boolean;
        data: { templates: GlobalTemplate[]; pagination: PaginationMeta };
      }>(`/api/templates?${params.toString()}`);

      set({
        templates: response.data.templates,
        pagination: response.data.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_TEMPLATES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load form templates',
        },
      });
    }
  },

  refreshTemplates: async () => {
    const { pagination } = get();
    await get().loadTemplates(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadTemplates(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadTemplates(1);
  },

  // ============================================
  // Active Template Actions
  // ============================================

  loadTemplate: async (id: string) => {
    set({ activeTemplateId: id, isLoadingTemplate: true, templateError: null });
    try {
      const api = getApiClient();
      const response = await api.get<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}`);

      set({ activeTemplate: response.data, isLoadingTemplate: false });
    } catch (error) {
      set({
        isLoadingTemplate: false,
        templateError: {
          code: 'LOAD_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load template',
        },
      });
    }
  },

  selectTemplate: (id: string) => {
    const { templates } = get();
    const template = templates.find((t) => t.id === id);
    if (template) {
      set({ activeTemplateId: id, activeTemplate: template });
    } else {
      get().loadTemplate(id);
    }
  },

  clearActiveTemplate: () => {
    set({
      activeTemplateId: null,
      activeTemplate: null,
      templateError: null,
    });
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createTemplate: async (dto: CreateFormTemplateDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>('/api/templates', {
        type: 'form',
        ...dto,
      });

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create form template',
        },
      });
      return null;
    }
  },

  updateTemplate: async (id: string, dto: UpdateFormTemplateDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}`, dto);

      // Update local state
      set((state) => ({
        templates: state.templates.map((t) =>
          t.id === id ? { ...t, ...response.data } : t
        ),
        activeTemplate:
          state.activeTemplate?.id === id
            ? { ...state.activeTemplate, ...response.data }
            : state.activeTemplate,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update form template',
        },
      });
      return false;
    }
  },

  duplicateTemplate: async (id: string, newName?: string): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<{
        success: boolean;
        data: GlobalTemplate;
      }>(`/api/templates/${id}/clone`, newName ? { name: newName } : {});

      // Refresh the list
      await get().refreshTemplates();
      set({ isPerformingAction: false });
      return response.data.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DUPLICATE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to duplicate form template',
        },
      });
      return null;
    }
  },

  deleteTemplate: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(`/api/templates/${id}`);

      // Remove from local state
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        activeTemplate: state.activeTemplate?.id === id ? null : state.activeTemplate,
        activeTemplateId: state.activeTemplateId === id ? null : state.activeTemplateId,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_TEMPLATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete form template',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, templateError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get templates by category */
export const useFormTemplatesByCategory = (category: string) => {
  return useFormTemplatesStore((state) =>
    state.templates.filter((t) => t.category === category)
  );
};

/** Get public templates count */
export const usePublicFormTemplatesCount = () => {
  return useFormTemplatesStore((state) =>
    state.templates.filter((t) => t.visibility === 'all').length
  );
};

/** Get active templates count */
export const useActiveFormTemplatesCount = () => {
  return useFormTemplatesStore((state) =>
    state.templates.filter((t) => t.isActive).length
  );
};

/** Check if any loading state is active */
export const useFormTemplatesLoading = () => {
  const isLoadingList = useFormTemplatesStore((state) => state.isLoadingList);
  const isLoadingTemplate = useFormTemplatesStore((state) => state.isLoadingTemplate);
  const isPerformingAction = useFormTemplatesStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingTemplate || isPerformingAction;
};

/** Get template by ID */
export const useFormTemplateById = (id: string) => {
  return useFormTemplatesStore((state) =>
    state.templates.find((t) => t.id === id)
  );
};

/** Get active filters count */
export const useActiveFormFiltersCount = () => {
  return useFormTemplatesStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.category) count++;
    if (state.filters.visibility) count++;
    if (state.filters.isActive !== undefined) count++;
    return count;
  });
};
